import { useMemo } from 'react';
import { createFileRoute, Link, Outlet, useChildMatches, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, UserPlus, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import {
  ArrowLeft,
  Mail,
  MailPlus,
  MoreHorizontal,
  Pencil,
  Send,
  Shield,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/atoms/avatar';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { AuditTrail } from '@/components/audit/AuditTrail';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { useWorkspaceHeader } from '@/components/layouts';
import {
  displayName,
  formatDate,
  formatStatus,
  initials,
} from '@/components/organisms/settings-users/user-display';
import { useUser } from '@/hooks/settings/useUser';
import { useRoles } from '@/hooks/settings/useRoles';
import { useUserGroups } from '@/hooks/settings/useUserGroups';
import { useDeleteUser } from '@/hooks/settings/useDeleteUser';
import { useResendInvitation } from '@/hooks/settings/useResendInvitation';
import { useCancelInvitation } from '@/hooks/settings/useCancelInvitation';
import { useHasPermission } from '@/hooks/usePermissions';
import type { TeamUser } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/users/$id')({
  component: UserSettingsDetailRoute,
});

function UserSettingsDetailRoute() {
  const { id } = Route.useParams();
  const router = useRouter();
  const childMatches = useChildMatches();
  const decodedId = decodeURIComponent(id);
  // Fetch the user directly by id — the paginated list only loads the first
  // page, so sourcing the detail from it breaks direct links to any user
  // beyond it.
  const { data: userData, isLoading: usersLoading } = useUser(decodedId);
  const { data: rolesData } = useRoles();
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData?.data]);

  const user = userData ?? null;

  const roleDescriptions = useMemo(() => {
    const descriptions = new Map<string, string | null>();
    roles.forEach((role) => descriptions.set(role.id, role.description));
    return descriptions;
  }, [roles]);

  // Let the edit child route (/$id/edit) mount through the Outlet instead of
  // always rendering the read-only detail (mirrors membership-plans/$id).
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <UserDetailContextHeaderSlot user={user} />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        {usersLoading ? (
          <p className="text-sm text-muted-foreground">Loading user...</p>
        ) : !user ? (
          <DetailCard label="User">
            <p className="text-sm text-muted-foreground">User not found.</p>
          </DetailCard>
        ) : (
          <UserDetailBody
            user={user}
            roleDescriptions={roleDescriptions}
            onBack={() => void router.navigate({ to: '/dashboard/settings/users' })}
          />
        )}
      </div>
    </div>
  );
}

function UserDetailBody({
  user,
  roleDescriptions,
  onBack,
}: {
  user: TeamUser;
  roleDescriptions: Map<string, string | null>;
  onBack: () => void;
}) {
  const isInvited = user.status === 'invited';

  return (
    <div className="space-y-4">
      <UserSummaryHeader user={user} onBack={onBack} />
      {isInvited ? <InvitedNextStepCard user={user} /> : null}
      <RolesSection user={user} roleDescriptions={roleDescriptions} />
      <AccountSection user={user} />
      <TechnicianLinkSection user={user} />
      <GroupsSection user={user} />
      <DetailCard label="Activity" icon={Users} bodyClassName="p-0">
        <AuditTrail entityType="user" entityId={user.id} alwaysOpen />
      </DetailCard>
    </div>
  );
}

// ─── Technician link (NOT-294) ─────────────────────────────────────────────────

function TechnicianLinkSection({ user }: { user: TeamUser }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  // POST /technicians requires technicians:create — gate the affordance on that
  // (not :edit) so it doesn't appear and then 403 in split permission sets.
  const canManageTechnicians = useHasPermission('technicians:create');

  const addTechnician = useApiMutation<{ id: string }, void>({
    mutationFn: () =>
      apiPost<{ id: string }>(operationsClient, schedulerEndpoints.technicians(), {
        authUserId: user.id,
        name: displayName(user),
        email: user.email,
      }),
    onSuccess: (technician) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.userDetail(user.id) });
      toast.success('Added to the Team as a technician');
      void router.navigate({
        to: '/dashboard/technicians/$id',
        params: { id: technician.id },
      });
    },
  });

  return (
    <DetailCard label="Team" icon={Wrench}>
      {user.technicianId ? (
        <p className="text-sm">
          On the Team roster.{' '}
          <Link
            to="/dashboard/technicians/$id"
            params={{ id: user.technicianId }}
            className="text-primary hover:underline"
          >
            View technician →
          </Link>
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Not on the Team roster yet.</p>
          {canManageTechnicians ? (
            <Button
              size="sm"
              onClick={() => addTechnician.mutate()}
              disabled={addTechnician.isPending}
            >
              {addTechnician.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <UserPlus className="size-4" />
              )}
              Add as Technician
            </Button>
          ) : null}
        </div>
      )}
    </DetailCard>
  );
}

// ─── Summary header ───────────────────────────────────────────────────────────

function UserSummaryHeader({ user, onBack }: { user: TeamUser; onBack: () => void }) {
  const router = useRouter();
  const canEdit = useHasPermission('users:edit');
  const canDelete = useHasPermission('users:delete');
  const canManageInvitations = useHasPermission('users:create');
  const deleteUser = useDeleteUser();
  const resendInvitation = useResendInvitation();
  const cancelInvitation = useCancelInvitation();
  const isInvited = user.status === 'invited';

  const roleNames = user.roles.map((role) => role.name).join(', ') || 'No roles';
  const hasOverflowActions = canDelete || (isInvited && canManageInvitations);

  return (
    <section className="rounded-xl border bg-card p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            className="-ml-1 shrink-0 md:hidden"
            onClick={onBack}
            aria-label="Back to users"
          >
            <ArrowLeft size={16} />
          </Button>
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarFallback>{initials(user)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-medium tracking-normal">{displayName(user)}</h1>
              <StatusBadge status={user.membershipStatus} className="rounded-md" />
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {canEdit ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void router.navigate({
                  to: '/dashboard/settings/users/$id/edit',
                  params: { id: user.id },
                })
              }
            >
              <Pencil className="size-4" />
              Edit
            </Button>
          ) : null}

          {hasOverflowActions ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" aria-label="More actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {isInvited && canManageInvitations ? (
                  <>
                    <DropdownMenuItem
                      disabled={resendInvitation.isPending}
                      onSelect={() => resendInvitation.mutate(user.id)}
                    >
                      <Send className="size-4" />
                      Resend invitation
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={cancelInvitation.isPending}
                      onSelect={() => cancelInvitation.mutate(user.id)}
                    >
                      <X className="size-4" />
                      Cancel invitation
                    </DropdownMenuItem>
                    {canDelete ? <DropdownMenuSeparator /> : null}
                  </>
                ) : null}
                {canDelete ? (
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={deleteUser.isPending}
                    onSelect={() =>
                      deleteUser.mutate(user.id, {
                        onSuccess: () => void router.navigate({ to: '/dashboard/settings/users' }),
                      })
                    }
                  >
                    <Trash2 className="size-4" />
                    Delete
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-4 text-sm text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <Mail className="size-4 shrink-0" />
          <span className="truncate">{user.email}</span>
        </span>
        <span>Roles: {roleNames}</span>
        <span>Member since {formatDate(user.createdAt)}</span>
      </div>
    </section>
  );
}

// ─── Invited next-step card ───────────────────────────────────────────────────

function InvitedNextStepCard({ user }: { user: TeamUser }) {
  const canManageInvitations = useHasPermission('users:create');
  const resendInvitation = useResendInvitation();

  const sentRelative = useMemo(() => {
    if (!user.invitedAt) return null;
    const date = new Date(user.invitedAt);
    if (Number.isNaN(date.getTime())) return null;
    return formatDistanceToNow(date, { addSuffix: true });
  }, [user.invitedAt]);

  const stateLabel = user.invitationStatus === 'expired' ? 'link expired' : 'awaiting acceptance';
  const headline = [
    sentRelative ? `Invitation sent ${sentRelative}` : 'Invitation sent',
    stateLabel,
  ].join(' · ');

  return (
    <section className="rounded-xl border border-warning/30 bg-warning/10 p-4 text-warning">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide">
            <MailPlus size={12} />
            Next step
          </p>
          <p className="mt-1 text-[15px] font-medium">{headline}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canManageInvitations ? (
            <Button
              variant="outline"
              size="sm"
              className="border-warning/40 bg-transparent text-warning hover:bg-warning/20 hover:text-warning"
              disabled={resendInvitation.isPending}
              onClick={() => resendInvitation.mutate(user.id)}
            >
              <Send className="size-4" />
              Resend
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function RolesSection({
  user,
  roleDescriptions,
}: {
  user: TeamUser;
  roleDescriptions: Map<string, string | null>;
}) {
  return (
    <DetailCard label={`Roles & permissions (${user.roles.length})`} icon={Shield}>
      {user.roles.length === 0 ? (
        <p className="text-sm text-muted-foreground">No roles assigned.</p>
      ) : (
        <div className="space-y-2">
          {user.roles.map((role) => {
            const description = roleDescriptions.get(role.id);
            return (
              <div key={role.id} className="rounded-md border bg-muted/20 p-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">{role.name}</p>
                </div>
                {description ? (
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </DetailCard>
  );
}

function AccountSection({ user }: { user: TeamUser }) {
  return (
    <DetailCard label="Account" icon={Shield}>
      <DetailFieldGrid className="sm:grid-cols-1">
        <DetailField label="Account status" value={formatStatus(user.status)} />
        <DetailField label="Membership" value={formatStatus(user.membershipStatus)} />
        <DetailField label="Created" value={formatDate(user.createdAt)} />
      </DetailFieldGrid>
    </DetailCard>
  );
}

function GroupsSection({ user }: { user: TeamUser }) {
  const router = useRouter();
  const { data: groups = [], isLoading } = useUserGroups(user.id);

  return (
    <DetailCard label={`Groups (${groups.length})`} icon={Users}>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading groups...</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">Not in any group</p>
      ) : (
        <div className="space-y-2">
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => void router.navigate({ to: '/dashboard/settings/groups' })}
              className="flex w-full items-center justify-between gap-2 rounded-md border bg-muted/20 p-3 text-left hover:bg-muted/40"
            >
              <span className="min-w-0 truncate text-sm font-medium">{group.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
              </span>
            </button>
          ))}
        </div>
      )}
    </DetailCard>
  );
}

// ─── Workspace header slot ────────────────────────────────────────────────────

function UserDetailContextHeaderSlot({ user }: { user: TeamUser | null }) {
  const contextHeader = useMemo(() => {
    if (!user) return null;
    return (
      <div className="flex min-w-0 flex-col">
        <h2 className="truncate text-base font-semibold">{displayName(user)}</h2>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
      </div>
    );
  }, [user]);

  useWorkspaceHeader({ contextHeader }, [contextHeader]);
  return null;
}
